/**
 * =============================================================================
 * IMPORTANT, READ THIS MESSAGE BEFORE EDITING THIS FILE.
 *
 * This file needs to be kept tightly in sync with its corresponding version in
 * the frontend. If you are changing this file, please also make the
 * corresponding changes in: `cip-backend:functions/src/karel/karelInterfaces.ts`
 * and submit both PRs simultaneously.
 *
 * Be careful because import paths may be different and the APIs for
 * `firebase-admin` are different from `firebase/firestore`. Don't forget to
 * double check that you update the comments in `cip-backend` as well.
 * =============================================================================
 */

import type {Dispatch, SetStateAction} from 'react';
import type {Karel} from './karelapi';

const KAREL_DIRECTIONS = ['North', 'East', 'South', 'West'] as const;
export type KarelDirection = (typeof KAREL_DIRECTIONS)[number];
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
export function isKarelDirection(input: any): input is KarelDirection {
  return (
    typeof input === 'string' &&
    KAREL_DIRECTIONS.includes(input as KarelDirection)
  );
}

export interface IKarelState {
  nRows: number;
  nCols: number;
  walls?: {
    [wallLoc: string]: boolean;
  };
  beepers?: {
    [beeperLoc: string]: number;
  };
  paint?: {
    [beeperLoc: string]: string;
  };
  karelRow: number;
  karelCol: number;
  karelDir: KarelDirection;
  actionHistory?: string[];
}

export function isIKarelState(testObject: unknown): testObject is IKarelState {
  if (!testObject || typeof testObject !== 'object') {
    return false;
  }
  const karelState = testObject as IKarelState;
  if (
    typeof karelState.nRows !== 'number' ||
    typeof karelState.nCols !== 'number' ||
    (karelState.walls && typeof karelState.walls !== 'object') ||
    (karelState.beepers && typeof karelState.beepers !== 'object') ||
    (karelState.paint && typeof karelState.paint !== 'object') ||
    typeof karelState.karelRow !== 'number' ||
    typeof karelState.karelCol !== 'number' ||
    !isKarelDirection(karelState.karelDir) ||
    (karelState.actionHistory && !Array.isArray(karelState.actionHistory))
  ) {
    return false;
  }
  if (karelState.nRows < 1 || karelState.nCols < 1) {
    console.warn(`Invalid world size: ${karelState.nRows}x${karelState.nCols}`);
  }
  for (const [wallLocation, doesExist] of Object.entries(
    karelState.walls ?? {},
  )) {
    if (typeof wallLocation !== 'string' || typeof doesExist !== 'boolean') {
      console.debug(
        `Invalid wall location: ${wallLocation}, doesExist: ${doesExist}. testObject: ${testObject}`,
      );
      return false;
    }
  }
  for (const [beeperLocation, count] of Object.entries(
    karelState.beepers ?? {},
  )) {
    if (typeof beeperLocation !== 'string' || typeof count !== 'number') {
      console.debug(
        `Invalid beeper location: ${beeperLocation}, count: ${count}. testObject: ${testObject}`,
      );
      return false;
    }
  }
  for (const [paintLocation, color] of Object.entries(karelState.paint ?? {})) {
    if (typeof paintLocation !== 'string' || typeof color !== 'string') {
      console.debug(
        `Invalid paint location: ${paintLocation}, color: ${color}. testObject: ${testObject}`,
      );
      return false;
    }
  }
  for (const action of karelState.actionHistory ?? []) {
    if (typeof action !== 'string') {
      console.debug(`Invalid action: ${action}. testObject: ${karelState}`);
      return false;
    }
  }
  return true;
}

export interface IKarelInfo {
  /**
   * The Karel instance (if it exists).
   */
  client: Karel | undefined;
  /**
   * Whether the Karel instance is active, i.e. whether or not Karel is
   * running.
   */
  active: boolean;
  /**
   * The number of times the Karel instance has been initialized. Used to ensure
   * that the Karel instance is only initialized once.
   */
  initCount: number;
  /**
   * The initial state of the Karel instance. This is used to reset the Karel
   * instance to its initial state.
   */
  startState: IKarelState;
  /**
   * The function that sets the state of the Karel instance.
   */
  setState: Dispatch<SetStateAction<IKarelState>>;
  /**
   * The time to sleep between steps of the Karel instance.
   */
  sleepTime: number;
}
